import { IDLE_ACTIVITY_STORAGE_KEYS } from '@supply-mover-2-0/core';
import {
	formatIdleSignOutMessage,
	IdleTimeoutGuard,
	useTranslation,
} from '@supply-mover-2-0/web-ui';
import type { JSX } from 'react';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	markAccessTokenIdleExpired,
	onIdleSessionExpired,
} from '../lib/portalIdleExpiry';
import { signOutPortal } from '../lib/portalSignOut';
import { useCartStore } from '../stores/cartStore';
import { useLoginModalStore } from '../stores/loginModalStore';
import { usePortalAuthStore } from '../stores/portalAuthStore';

/**
 * PD-16414 — the storefront's idle sign-out. Mounted once inside TenantGate.
 *
 * The limit is the tenant's `security.idle_timeout.storefrontMinutes`, which
 * arrives with /auth/me; the guard waits until it does and does nothing when
 * it is off. On a sign-out the shopper lands on the home page with the
 * sign-in dialog open, saying why, which is the same place the Sign Out menu
 * item leaves them.
 *
 * `local`: this tab reached the limit, so it revokes the session. `remote`:
 * another tab's sign-out was heard first; this one only clears itself and
 * shows the same message. (Two tabs reaching the limit in the same instant
 * both revoke the same tokens, which is harmless.)
 *
 * PD-17338 — a session found idle-expired BEFORE it was used (at start-up,
 * or by the first request after the app comes back from the background; see
 * portalIdleExpiry) was already cleared without a request going out. It ends
 * the same way as a local sign-out here: revoked, with the same message.
 */
export function PortalIdleTimeout(): JSX.Element {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const isAuthenticated = usePortalAuthStore((s) => s.isAuthenticated);
	const limitMinutes = usePortalAuthStore((s) => s.idleTimeoutMinutes);

	// What every idle sign-out shows. Held in a ref so the expiry
	// subscription below is made once and still uses the current `t` and
	// `navigate`.
	const showSignedOut = (minutes: number | null | undefined): void => {
		const message = formatIdleSignOutMessage(t, minutes ?? null);
		useCartStore.getState().setCart(null);
		void navigate('/');
		useLoginModalStore.getState().open(message);
	};
	const showSignedOutRef = useRef(showSignedOut);
	showSignedOutRef.current = showSignedOut;

	useEffect(
		() =>
			onIdleSessionExpired((session) => {
				void signOutPortal({
					revoke: true,
					session: { token: session.token, refreshToken: session.refreshToken },
				});
				showSignedOutRef.current(session.limitMinutes);
			}),
		[],
	);

	return (
		<IdleTimeoutGuard
			isAuthenticated={isAuthenticated}
			limitMinutes={limitMinutes}
			storageKey={IDLE_ACTIVITY_STORAGE_KEYS.storefront}
			onTimeout={(source) => {
				// A request built before the sign-out must not send this token.
				markAccessTokenIdleExpired(usePortalAuthStore.getState().token);
				void signOutPortal({ revoke: source === 'local' });
				showSignedOut(limitMinutes);
			}}
		/>
	);
}
